import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Package, FileText, ArrowRight, CheckCircle2, Clock } from "lucide-react";
import api from "@/lib/api";
import { CURRENCY, tStep } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";

const C = {
  fr: { hello: "Bonjour", overview: "Voici un aperçu de votre espace client.",
    reservations: "Réservations", activeFiles: "Dossiers actifs", documents: "Documents",
    myReservations: "Mes réservations", reservePack: "Réserver un pack", none: "Aucune réservation pour le moment",
    noneText: "Découvrez nos packs et commencez votre projet.", seePacks: "Voir nos packs",
    follow: "Suivre", pending: "En attente de paiement" },
  en: { hello: "Hello", overview: "Here is an overview of your client area.",
    reservations: "Bookings", activeFiles: "Active files", documents: "Documents",
    myReservations: "My bookings", reservePack: "Book a package", none: "No booking yet",
    noneText: "Discover our packages and start your project.", seePacks: "See our packages",
    follow: "Track", pending: "Awaiting payment" },
};

export default function DashboardHome() {
  const { user } = useAuth();
  const { lang } = useLang();
  const c = C[lang];
  const [bookings, setBookings] = useState([]);
  const [docCount, setDocCount] = useState(0);

  useEffect(() => {
    api.get("/bookings/me").then((r) => setBookings(r.data)).catch(() => {});
    api.get("/documents/me").then((r) => setDocCount(r.data.length)).catch(() => {});
  }, []);

  const paid = bookings.filter((b) => b.payment_status === "paid");

  return (
    <div data-testid="dashboard-home">
      <h1 className="font-heading text-3xl sm:text-4xl text-navy">{c.hello}, {user?.name?.split(" ")[0]} 👋</h1>
      <p className="text-muted-foreground mt-2">{c.overview}</p>

      <div className="grid sm:grid-cols-3 gap-5 mt-8">
        {[
          { label: c.reservations, value: bookings.length, icon: Package },
          { label: c.activeFiles, value: paid.length, icon: CheckCircle2 },
          { label: c.documents, value: docCount, icon: FileText },
        ].map((s) => (
          <div key={s.label} className="bg-white border border-border p-6">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">{s.label}</span>
              <s.icon size={20} className="text-gold" />
            </div>
            <div className="font-heading text-4xl text-navy mt-3">{s.value}</div>
          </div>
        ))}
      </div>

      <div className="mt-10">
        <div className="flex items-center justify-between">
          <h2 className="font-heading text-2xl text-navy">{c.myReservations}</h2>
          <Link to="/nos-packs" className="text-sm text-gold font-medium hover:underline flex items-center gap-1">{c.reservePack} <ArrowRight size={14} /></Link>
        </div>

        {bookings.length === 0 ? (
          <div className="bg-white border border-border p-10 text-center mt-5">
            <Package size={40} className="mx-auto text-gold" />
            <p className="text-navy font-medium mt-4">{c.none}</p>
            <p className="text-muted-foreground text-sm mt-1">{c.noneText}</p>
            <Link to="/nos-packs" className="inline-block mt-5 bg-gold hover:bg-gold-hover text-white px-6 py-3 font-medium transition-colors">{c.seePacks}</Link>
          </div>
        ) : (
          <div className="space-y-4 mt-5">
            {bookings.map((b) => (
              <div key={b.id} data-testid={`booking-${b.id}`} className="bg-white border border-border p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <h3 className="font-heading text-xl text-navy">{b.pack_name}</h3>
                  <p className="text-sm text-muted-foreground mt-1">{CURRENCY(b.amount)}</p>
                </div>
                <div className="flex items-center gap-4">
                  {b.payment_status === "paid" ? (
                    <span className="flex items-center gap-2 text-sm text-green-700 bg-green-50 px-3 py-1.5"><CheckCircle2 size={15} /> {tStep(b.steps?.[b.step_index], lang)}</span>
                  ) : (
                    <span className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 px-3 py-1.5"><Clock size={15} /> {c.pending}</span>
                  )}
                  <Link to="/dashboard/suivi" className="text-sm text-gold font-medium hover:underline">{c.follow}</Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
